import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { ProformaRepository } from 'src/app/proforma/domain/proforma.repository';
import { DialogModule } from 'primeng/dialog';
import { TreeTable, TreeTableModule } from 'primeng/treetable';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { UtilService } from 'src/app/services/util.service';
import { TextareaModule } from 'primeng/textarea';
import { CustomPicklistComponent } from 'src/app/shared/components/custom-picklist/custom-picklist.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { StorageService } from 'src/app/services/storage.service';
import { TabsModule } from 'primeng/tabs';
import { ClientesComerciales, GrupoComercial, ListarClientesComercialesResponse, TipoCCSDTO } from 'src/app/proforma/domain/response/proforma_response';
import { Router } from '@angular/router';
import { MultiSelectModule } from 'primeng/multiselect';
import { CommonModule } from '@angular/common';
import { PaginatorState } from 'primeng/paginator';
import { RippleModule } from 'primeng/ripple';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { RadioButtonModule } from 'primeng/radiobutton';

interface Grupo {
  grupo_id: number;
  nombre_grupo: string;
  expandido: boolean;
  clientes: ClientesComerciales[];
}
// Para usar en el tipo de las señales
type ItemAsignacion = ClientesComerciales | Grupo;

// Interfaz para manejar la información de la combinación
interface RowExportData {
  data: any; // El objeto plano con los datos de la fila
  isGroup: boolean; // Indica si es una fila de grupo
  groupStartIndex?: number; // Índice de inicio de la combinación (fila)
  groupEndIndex?: number; // Índice de fin de la combinación (fila)
  groupName?: string; // Nombre del grupo
}

@Component({
  selector: 'app-grupos-comerciales',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DialogModule,
    TreeTableModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    CustomPicklistComponent,
    TabsModule,
    MultiSelectModule,
    CommonModule,
    RippleModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    SelectButtonModule,
    FormsModule,
    RadioButtonModule
],
  templateUrl: './grupos-comerciales.component.html',
  styleUrl: './grupos-comerciales.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruposComercialesComponent implements OnInit{

  private fb = inject(FormBuilder);
  private gruposService = inject(ProformaRepository);
  private util = inject(UtilService);
  private storage = inject(StorageService);
  private router = inject(Router);

  navegar(ruta: string) {
    this.router.navigate([ruta]);
  }

  // señales para inputs
  grupoValue = signal<string>('');
  razonValue = signal<string>('');
  rucValue = signal<string>('');

  filterMode: 'lenient' | 'strict' = 'lenient';

  filterModes = [
    { label: 'Flexible', value: 'lenient' },
    { label: 'Estricto', value: 'strict' }
  ];
  // Referencia del TreeTable (usada para el filtrado global)
  @ViewChild('tt') tt!: TreeTable;

  // Ejemplo en tu componente .ts
  filtroTipo: 'grupo' | 'cliente' = 'grupo'; // valor por defecto

  filtroTexto: string = '';

  campoAnterior: string = 'nombre_grupo'; // valor inicial por defecto

  aplicarFiltro(tt: any) {
    const campoActual = this.filtroTipo === 'grupo' ? 'nombre_grupo' : 'razon_social';

    // 🔹 Si cambiaste de tipo de filtro, elimina el filtro anterior
    if (this.campoAnterior !== campoActual && tt.filters) {
      delete tt.filters[this.campoAnterior];
    }

    // 🔹 Aplica el nuevo filtro sobre el campo seleccionado
    tt.filter(this.filtroTexto, campoActual, 'contains');

    // 🔹 Guarda el campo actual como referencia
    this.campoAnterior = campoActual;
  }
  /**
   * Validador personalizado que asegura que al menos uno de los dos campos
   * (categoriaEjecutivos o categoriaEjecutivosClientes) tiene un valor (no es null ni array vacío).
   */
  alMenosUnCampoRequerido: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormGroup)) {
      return null; // Solo aplica a FormGroups
    }

    const ejecutivosControl = control.get('categoriaEjecutivos');
    const clientesControl = control.get('categoriaEjecutivosClientes');

    // Si los controles no existen, evita un error de ejecución
    if (!ejecutivosControl || !clientesControl) {
      return null;
    }

    // 1. Obtener los valores (serán arrays o null)
    const ejecutivosValor = ejecutivosControl.value;
    const clientesValor = clientesControl.value;

    // 2. Verificar si alguno tiene un valor válido (no nulo y no array vacío)
    const ejecutivosTieneValor = ejecutivosValor && ejecutivosValor.length > 0;
    const clientesTieneValor = clientesValor && clientesValor.length > 0;

    // Si AL MENOS UNO tiene valor, la validación pasa (retorna null)
    if (ejecutivosTieneValor || clientesTieneValor) {
      return null;
    }

    // Si AMBOS son nulos o arrays vacíos, la validación falla (retorna un objeto de error)
    return { alMenosUno: true };
  };

  // formulario de creación/edición
  groupEjecutivos: FormGroup = this.fb.group({
    categoriaEjecutivos: [['001', '002','003']],
    categoriaEjecutivosClientes: [null],
  },{
    // APLICAR el validador a nivel de FormGroup
    validators: this.alMenosUnCampoRequerido
  });

  // Detectar si hay algún campo con valor
  campoActivo = computed(() => {
    return [this.grupoValue(), this.razonValue(), this.rucValue()]
      .some(v => v.length > 0);
  });

  // Función para deshabilitar inputs según el valor actual
  isDisabled = (campo: 'grupo' | 'razon' | 'ruc') => {
    if (!this.campoActivo()) return false; // si ninguno tiene valor, todo habilitado
    return (
      (campo !== 'grupo' && this.grupoValue().length > 0) ||
      (campo !== 'razon' && this.razonValue().length > 0) ||
      (campo !== 'ruc' && this.rucValue().length > 0)
    );
  };

  categoriaEjecutivos = [
    { value: '001', label: 'Con más de un ejecutivo' },
    { value: '002', label: 'Con un solo ejecutivo' },
    { value: '003', label: 'Sin ejecutivos' },
  ];
  categoriaEjecutivosClientes = [
    { value: '001', label: 'Con ejecutivo' },
    { value: '002', label: 'Sin ejecutivo' },
  ];

  // signals
  gruposTree = signal<TreeNode<GrupoComercial>[]>([]);
  loading = signal(false);
  showDialog = false;

  ListarTipoCliente: TipoCCSDTO[] = []

  // Signals para la lista
  allAvailableClients = signal<ItemAsignacion[]>([]); // Todos los clientes disponibles sin filtrar
  selectedClients = signal<ItemAsignacion[]>([]); // Clientes seleccionados

  // Señales para los filtros
  sourceFilter = signal<string>('');
  targetFilter = signal<string>('');

  // Nuevo signal para el filtro del select
  clienteFilter = signal<string>('');

  // Signal para la pestaña seleccionada
  selectedTab = signal<number>(0);

  // Signals para los mensajes de validación separados
  validationGeneralMessage = signal<string | null>(null);
  ejecutivosValidationMessage = signal<string | null>(null);
  gruposValidationMessage = signal<string | null>(null);

  // Signals para el diálogo de autenticación
  showAuthDialog = signal<boolean>(false);
  adminUsername = signal<string>('');
  adminPassword = signal<string>('');
  authError = signal<string>('');

  // Señal computada para generar las pestañas dinámicamente
  validationTabs = computed(() => {
    const tabs = [];
    if (this.ejecutivosValidationMessage()) {
      tabs.push({
        value: 0,
        title: 'Validación de Ejecutivos',
        content: this.ejecutivosValidationMessage()!
      });
    }
    if (this.gruposValidationMessage()) {
      tabs.push({
        value: this.ejecutivosValidationMessage() ? 1 : 0,
        title: 'Validación de Grupos',
        content: this.gruposValidationMessage()!
      });
    }
    return tabs;
  });

  // **Nuevas señales para los checkboxes de filtro por tipo**
  filtroNoAsignadosPorGrupo = signal<boolean>(true);
  filtroNoAsignadosPorCliente = signal<boolean>(true);

  // Propiedades computadas para el filtrado reactivo
  filteredSourceItems = computed(() => {
    const textFilter = this.sourceFilter().toLowerCase();
    const showGroups = this.filtroNoAsignadosPorGrupo();
    const showClients = this.filtroNoAsignadosPorCliente();
    const selectFilter = this.clienteFilter();
    const originalList = this.allAvailableClients();

    // Si no se seleccionó ningún tipo, mostrar todo por defecto
    if (!showGroups && !showClients) {
        return [];
    }

    // Filtrado por tipo de ítem (grupo/cliente)
    let filteredByType = originalList.filter(item => {
        const isGroup = this.esGrupo(item);
        return (isGroup && showGroups) || (!isGroup && showClients);
    });

    // ✅ Filtrado por select (codigo_cliente)
    if (selectFilter) {
      filteredByType = filteredByType.filter(item => {
        if (this.esGrupo(item)) {
          // incluir el grupo si al menos un cliente cumple
          return item.clientes.some(c => c.codigo_cliente === selectFilter);
        } else {
          return item.codigo_cliente === selectFilter;
        }
      });
    }

    // Filtrado por término de búsqueda
    if (textFilter) {
      filteredByType = filteredByType.filter(item => {
        if (this.esGrupo(item)) {
          return item.nombre_grupo.toLowerCase().includes(textFilter);
        } else {
          return (
            item.razon_social.toLowerCase().includes(textFilter) ||
            item.entC_DocIden.toLowerCase().includes(textFilter)
          );
        }
      });
    }
    return filteredByType;

  });


    // return this.allAvailableClients().filter(item => {
    //   // Filtrar por el input de texto
    //   const isTextMatch = item['filtro_cliente']?.toString().toLowerCase().includes(textFilter);
    //   // Filtrar por el valor del select. Si el selectFilter es vacío, no se aplica este filtro.
    //   const isSelectMatch = !selectFilter || item['codigo_cliente'] === selectFilter;

    //   return isTextMatch && isSelectMatch;
    // });


  filteredTargetItems = computed(() => {
    const filter = this.targetFilter().toLowerCase();
    const originalList = this.selectedClients();

    let filteredByType = originalList
    // Filtrado por término de búsqueda
    if (filter) {
      filteredByType = filteredByType.filter(item => {
        if (this.esGrupo(item)) {
          return item.nombre_grupo.toLowerCase().includes(filter);
        } else {
          return (
            item.razon_social.toLowerCase().includes(filter) ||
            item.entC_DocIden.toLowerCase().includes(filter)
          );
        }
      });
    }
    return filteredByType;
  });

  // Métodos que manejan los eventos emitidos por el CustomPicklistComponent
  // onSelectItem(item: any) {
    // this.allAvailableClients.update(items => items.filter(i => i.entC_Codigo !== item.entC_Codigo));
    // this.selectedClients.update(items => [item, ...items ]);
  // }

  // Añadir la función auxiliar para encontrar un cliente en la lista original completa
  obtenerClienteOriginal(entC_Codigo: number): ClientesComerciales | undefined {
    const data = this.allAvailableClients();
    for (const item of data) {
      if (this.esGrupo(item)) {
        const clienteEnGrupo = item.clientes.find(c => c.entC_Codigo === entC_Codigo);
        if (clienteEnGrupo) return clienteEnGrupo;
      } else if (item.entC_Codigo === entC_Codigo) {
        return item as ClientesComerciales;
      }
    }
    return undefined;
  }

  // Los clientes que llegan con tipo: "seleccionado" siempre se tratarán como clientes sueltos.
  // Nunca serán fusionados en grupos dentro de selectedClients ni en allAvailableClients.
  // Los demás clientes seguirán respetando la lógica de agrupar/desagrupar por grupo_id.
  onSelectItem(item: ItemAsignacion) {
    // 🚀 Caso especial: clientes "seleccionado"
    if (!this.esGrupo(item) && (item as ClientesComerciales).tipo === "seleccionado") {
      const cliente = item as ClientesComerciales;

      // Sacar de disponibles
      this.allAvailableClients.update(disponibles =>
        disponibles.filter(
          i => !( !this.esGrupo(i) && (i as ClientesComerciales).entC_Codigo === cliente.entC_Codigo )
        )
      );

      // Agregar como cliente simple a seleccionados
      this.selectedClients.update(seleccionados => [cliente, ...seleccionados]);

      console.log("onSelectItem [seleccionado]");
      console.log("allAvailableClients", this.allAvailableClients());
      console.log("selectedClients", this.selectedClients());
      return;
    }

    // 🚀 Lógica ordinaria para grupos y clientes "disponible"
    if (this.esGrupo(item)) {
      // Caso 1: se selecciona un grupo completo
      this.allAvailableClients.update(disponibles =>
        disponibles.filter(i => !(this.esGrupo(i) && i.grupo_id === item.grupo_id))
      );

      this.selectedClients.update(seleccionados => {
        if (!item.grupo_id || !item.nombre_grupo) {
          // 🚫 No agrupar si no tiene grupo_id
          return [...item.clientes, ...seleccionados];
        }

        const grupoExistente = seleccionados.find(
          i => this.esGrupo(i) && i.grupo_id === item.grupo_id
        ) as Grupo | undefined;

        if (grupoExistente) {
          grupoExistente.clientes.push(...item.clientes);
          grupoExistente.clientes = this.eliminarDuplicados(
            grupoExistente.clientes,
            "entC_Codigo"
          );
          return [...seleccionados];
        } else {
          const clientesSueltos = seleccionados.filter(
            i => !this.esGrupo(i) && (i as ClientesComerciales).grupo_id === item.grupo_id
          ) as ClientesComerciales[];

          if (clientesSueltos.length > 0) {
            const nuevoGrupo: Grupo = {
              grupo_id: item.grupo_id,
              nombre_grupo: item.nombre_grupo,
              expandido: true,
              clientes: this.eliminarDuplicados(
                [...clientesSueltos, ...item.clientes],
                "entC_Codigo"
              ),
            };

            const sinClientesSueltos = seleccionados.filter(
              i => this.esGrupo(i) || (i as ClientesComerciales).grupo_id !== item.grupo_id
            );

            return [nuevoGrupo, ...sinClientesSueltos];
          } else {
            return [item, ...seleccionados];
          }
        }
      });
    } else {
      // Caso 2: se selecciona un cliente individual (ordinario)
      const cliente = item as ClientesComerciales;

      this.allAvailableClients.update(disponibles => {
        if (!cliente.grupo_id) {
          // 🚫 Cliente sin grupo → eliminar directo
          return disponibles.filter(
            i =>
              !(
                !this.esGrupo(i) &&
                (i as ClientesComerciales).entC_Codigo === cliente.entC_Codigo
              )
          );
        }
        // Cliente con grupo → actualizar ese grupo
        return disponibles
          .map(i => {
            if (this.esGrupo(i) && i.grupo_id === cliente.grupo_id) {
              i.clientes = i.clientes.filter(
                c => c.entC_Codigo !== cliente.entC_Codigo
              );
            }
            return i;
          })
          .filter(
            i =>
              !(
                this.esGrupo(i) &&
                i.grupo_id === cliente.grupo_id &&
                i.clientes.length === 0
              )
          );
      });

      this.selectedClients.update(seleccionados => {
        if (!cliente.grupo_id) {
          // 🚫 Cliente sin grupo → añadirlo como cliente simple
          return [cliente, ...seleccionados];
        }
        const grupoExistente = seleccionados.find(
          i => this.esGrupo(i) && i.grupo_id === cliente.grupo_id
        ) as Grupo | undefined;

        if (grupoExistente) {
          grupoExistente.clientes.push(cliente);
          grupoExistente.clientes = this.eliminarDuplicados(
            grupoExistente.clientes,
            "entC_Codigo"
          );
          return [...seleccionados];
        } else {
          const clientesSueltos = seleccionados.filter(
            i =>
              !this.esGrupo(i) &&
              (i as ClientesComerciales).grupo_id === cliente.grupo_id
          ) as ClientesComerciales[];

          if (clientesSueltos.length > 0) {
            const nuevoGrupo: Grupo = {
              grupo_id: cliente.grupo_id,
              nombre_grupo: cliente.nombre_grupo,
              expandido: true,
              clientes: this.eliminarDuplicados(
                [cliente, ...clientesSueltos],
                "entC_Codigo"
              ),
            };

            const sinClientesSueltos = seleccionados.filter(
              i =>
                this.esGrupo(i) ||
                (i as ClientesComerciales).grupo_id !== cliente.grupo_id
            );

            return [nuevoGrupo, ...sinClientesSueltos];
          } else {
            return [cliente, ...seleccionados];
          }
        }
      });
    }

    console.log("onSelectItem [ordinario]");
    console.log("allAvailableClients", this.allAvailableClients());
    console.log("selectedClients", this.selectedClients());
  }



  onDeselectItem(item: ItemAsignacion) {
    if (this.esGrupo(item)) {
      // Caso 1: se deselecciona un grupo completo
      this.selectedClients.update(items =>
        items.filter(i => !this.esGrupo(i) || i.grupo_id !== item.grupo_id)
      );

      this.allAvailableClients.update(items => {
        const grupoExistente = items.find(i => this.esGrupo(i) && i.grupo_id === item.grupo_id) as Grupo | undefined;

        if (grupoExistente) {
          // 🔑 Fusionar clientes en el grupo ya existente
          grupoExistente.clientes.push(...item.clientes);

          // Evitar duplicados de clientes
          grupoExistente.clientes = this.eliminarDuplicados(grupoExistente.clientes, "entC_Codigo");

          return [...items];
        } else {
          // Si no existe, insertar el grupo
          return [item, ...items ];
        }
      });

    } else {
      // Caso 2: se deselecciona un cliente individual
      const clienteDeseleccionado = item as ClientesComerciales;

      this.selectedClients.update(items =>
        items.filter(i => this.esGrupo(i) || i.entC_Codigo !== clienteDeseleccionado.entC_Codigo)
      );

      if (clienteDeseleccionado.grupo_id && clienteDeseleccionado.tipo !== "seleccionado") {
        const grupoId = clienteDeseleccionado.grupo_id;
        const nombreGrupo = clienteDeseleccionado.nombre_grupo;

        this.allAvailableClients.update(items => {
          const grupoExistente = items.find(i => this.esGrupo(i) && i.grupo_id === grupoId) as Grupo | undefined;

          if (grupoExistente) {
            grupoExistente.clientes.push(clienteDeseleccionado);

            // 🔑 Evitar duplicados dentro del grupo
            grupoExistente.clientes = this.eliminarDuplicados(grupoExistente.clientes, "entC_Codigo");

            return [...items];
          } else {
            const nuevoGrupo: Grupo = {
              grupo_id: grupoId,
              nombre_grupo: nombreGrupo,
              expandido: false,
              clientes: [clienteDeseleccionado],
            };
            return [nuevoGrupo, ...items ];
          }
        });
      } else {
        // 🚀 Si es tipo "seleccionado", siempre regresa como cliente suelto
        this.allAvailableClients.update(items => [clienteDeseleccionado, ...items]);
      }
    }

    console.log("onDeselectItem");
    console.log("allAvailableClients", this.allAvailableClients());
    console.log("selectedClients", this.selectedClients());
  }

  // 🔧 Helper para eliminar duplicados dentro de un grupo
  private eliminarDuplicados<T>(arr: T[], key: keyof T): T[] {
    const seen = new Set();
    return arr.filter(obj => {
      const val = obj[key];
      if (seen.has(val)) return false;
      seen.add(val);
      return true;
    });
  }

  onSelectFilterChange(value: string) {
    this.clienteFilter.set(value);
  }

  // formulario de creación/edición
  grupoForm: FormGroup = this.fb.group({
    grupo_id: [null],
    nombre_grupo: ['', [Validators.required, Validators.minLength(3)]],
    descripcion: ['']
  });

  dialogTitle = signal<string>('');

  async ngOnInit() {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.buscarGrupos()
    this.ListarTipoCliente = await this.util.loadTiposClientes()
    this.ListarTipoCliente.push({ name: 'FALTA REVISAR', value: '404' });
    console.log("Tipos de Cliente:", this.ListarTipoCliente);
  }

  totalGrupos: number = 0
  // Inicialmente en la primera página
  first: number = 0;
  // Define cuántas filas por página quieres mostrar
  rows: number = 20;

  // Esta función se activará cada vez que la página cambie
  onPageChange(event: PaginatorState): void {
    // Sincroniza las variables con el estado actual de la paginación
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 0;

    console.log('El usuario ha cambiado a la página:', event.page);
    console.log('Primer registro en la página:', this.first);
  }

  // Función para saltar a la página 6
  saltarAPaginaSeis(): void {
    // Calcula el índice del primer registro para la página 6 (índice 5)
    this.first = (6 - 1) * this.rows;
  }

  buscarGrupos(grupo_id?: number) {

    this.loading.set(true);
    this.gruposService.ListarGruposComerciales({grupo_id, nombre_grupo: this.grupoValue().trim(), razon_social: this.razonValue().trim(), ruc: this.rucValue().trim()
      , categoriaEjecutivos: this.groupEjecutivos.get("categoriaEjecutivos")?.value
      , categoriaEjecutivosClientes: this.groupEjecutivos.get("categoriaEjecutivosClientes")?.value
    }).subscribe({
      next: (res) => {
        this.first = 0;
        this.totalGrupos = res.datos.TotalRegistros
        this.gruposTree.set(res.datos.result as TreeNode<GrupoComercial>[]);
        console.log("gruposTree", this.gruposTree())
        this.loading.set(false);
      },
      error: (error) => {
        this.loading.set(false);
        if (error.status === 404) {
          this.util.ShowMessage({
            titulo: 'No se encontraron clientes',
            mensaje:
              'No se encontraron clientes con los criterios de búsqueda proporcionados.',
            tipo: 'warning',
            autocerrar: true,
            tiempo: 5000,
          });
        } else {
          this.util.ShowMessage({
            titulo: 'Error de Conexión',
            mensaje:
              'No se pudo conectar con el servidor para cargar los clientes.',
            detalle: error.message || 'Error desconocido.',
            tipo: 'error',
            autocerrar: false,
          });
        }
      },
    });
  }

  // Método para abrir el diálogo
  abrirDialogCrear() {
    this.grupoForm.reset();
    this.selectedClients.set([]);
    this.dialogTitle.set('Crear Nuevo Grupo Comercial');
    this.sourceFilter.set("")
    this.targetFilter.set("")
    this.clienteFilter.set("")
    this.filtroNoAsignadosPorGrupo.set(true)
    this.filtroNoAsignadosPorCliente.set(true)
    this.cargarClientesDisponibles();
    this.showDialog = true;
  }

  abrirDialogEditar(grupo: any) {
    this.grupoForm.patchValue({
      grupo_id: grupo.grupo_id,
      nombre_grupo: grupo.nombre_grupo,
      descripcion: grupo.descripcion
    });
    const grupoForm = this.grupoForm.value;
    this.dialogTitle.set(`Editar Grupo: ${grupoForm.nombre_grupo}`);
    this.sourceFilter.set("")
    this.targetFilter.set("")
    this.clienteFilter.set("")
    this.filtroNoAsignadosPorGrupo.set(true)
    this.filtroNoAsignadosPorCliente.set(true)
    this.cargarClientesDisponibles(grupo.grupo_id);
    this.showDialog = true;
  }

  eliminarGrupo(grupo: GrupoComercial) {
    this.util.ShowMessage({
      titulo: '¿Eliminar grupo?',
      mensaje: `¿Está seguro de eliminar el grupo ${grupo.nombre_grupo}?`,
      tipo: 'warning',
      pregunta: {
        onAccept: () => {
          // Aquí va la lógica real de eliminación
          this.gruposService.EliminarGrupoComercial(grupo.grupo_id).subscribe({
            next: () => {
              this.util.ShowMessage({
                titulo: 'Eliminado',
                mensaje: `El grupo ${grupo.nombre_grupo} fue eliminado correctamente.`,
                tipo: 'ok',
                autocerrar: true,
                tiempo: 3000
              });
              // this.buscarGrupos();
              // Optimización: Actualiza el signal localmente
              this.gruposTree.update(tree =>
                tree.filter(node => node.data.grupo_id !== grupo.grupo_id)
              );
              if(this.gruposTree().length === 0){
                this.buscarGrupos();
              }
            },
            error: (error) => {
              this.handleGrupoError(error, 'eliminar');
            }
          });

        },
        onReject: () => {
          this.util.ShowMessage({
            titulo: 'Cancelado',
            mensaje: 'La eliminación fue cancelada.',
            tipo: 'warning',
            autocerrar: true,
            tiempo: 3000
          });
        }
      },
      textoBtn: {
        textoBtnOpcion1: 'Sí, eliminar',
        textoBtnOpcion2: 'No, cancelar'
      }
    });
  }

  // get modifiedClientsExist(){
  //   const selected = this.selectedClients();

  //   return selected.length > 1 || this.grupoForm.invalid
  // }

  mensajeActualizacion: string = "";

  get disableGuardar(): boolean {

    const clientesSeleccionados = this.selectedClients();
    const clientesTotales = clientesSeleccionados.flatMap(item => {
      if (this.esGrupo(item)) {
        // Si es un grupo, devolver su array de clientes
        return item.clientes;
      } else {
        // Si es un cliente, devolverlo en un array para que flatMap lo maneje correctamente
        return [item];
      }
    });
    const grupo_id = this.grupoForm.get('grupo_id').value;
    // 📌 Si es CREACIÓN → exigir mínimo 2 clientes
    if (!grupo_id) {
      return this.grupoForm.invalid || clientesTotales.length < 2;
    }

    // 📌 Si es ACTUALIZACIÓN → solo deshabilitar si el formulario es inválido
    return this.grupoForm.invalid;
  }


  guardarGrupo() {
    //! Quitamos las validaciones para que se puedan organizar bien los grupos.
    this.guardar();
    return
    const selected = this.selectedClients();

    //* Puede que solo modifiquen el nombre del grupo o descripción, por lo que es innecesario colocar esta validación.
    // Si no hay clientes seleccionados, no hay nada que validar
    // if (selected.length === 0) {
    //   this.validationGeneralMessage.set("Se guardará el grupo sin ningún cliente, está seguro de continuar?")
    //   this.showAuthDialog.set(true);
    //   return
    // }

    // Asignar los resultados de la validación a las señales correspondientes
    // this.ejecutivosValidationMessage.set(this.validateEjecutivos());
    // this.gruposValidationMessage.set(this.validateGrupos());

    if (this.ejecutivosValidationMessage() || this.gruposValidationMessage()) {
      // Si alguna validación falla, mostrar el diálogo de autenticación
      this.showAuthDialog.set(true);
    } else {
      // Si ambas validaciones pasan, guardar
      // this.guardar();
    }
  }

  // private validateEjecutivos(): string | null {
  //   const selected = this.selectedClients();
  //   const existingSelectedClients = selected.filter(c => c.tipo === 'seleccionado' && c.codigoEjecutivo !== null);
  //   const newlyAddedClients = selected.filter(c => c.tipo === 'disponible' && c.codigoEjecutivo !== null);

  //   // Si no hay nuevos clientes para validar, la validación pasa
  //   if (newlyAddedClients.length === 0) {
  //     return null;
  //   }

  //   // Obtener los códigos de ejecutivo únicos de los clientes ya seleccionados (que no son null)
  //   const existingExecutiveCodes = new Set(existingSelectedClients.map(item => item.codigoEjecutivo));

  //   // Si no hay ejecutivos en la lista original, no hay validación por código
  //   if (existingExecutiveCodes.size === 0) {
  //     return null;
  //   }

  //   // Validar si los nuevos clientes tienen un código de ejecutivo diferente a los existentes
  //   const mismatchedNewClients = newlyAddedClients.filter(item =>
  //     !existingExecutiveCodes.has(item.codigoEjecutivo)
  //   );

  //   if (mismatchedNewClients.length > 0) {
  //     let message = 'Advertencia: Se están agregando clientes con un "código de ejecutivo" diferente a los ya seleccionados.\n\n';

  //     message += 'Clientes con diferentes ejecutivos agregados:\n';
  //     mismatchedNewClients.forEach(client => {
  //       message += `- ${client.razon_social} (${client.nomCompletoEjecutivo || 'SIN EJECUTIVO'})\n`;
  //     });

  //     message += `\nClientes de la lista original:\n`;
  //     existingSelectedClients.forEach(client => {
  //       message += `- ${client.razon_social} (${client.nomCompletoEjecutivo || 'SIN EJECUTIVO'})\n`;
  //     });

  //     return message;
  //   }

  //   return null;
  // }

  // private validateGrupos(): string | null {
  //   const selected = this.selectedClients();
  //   const existingSelectedClients = selected.filter(c => c.tipo === 'seleccionado');
  //   const newlyAddedClients = selected.filter(c => c.tipo === 'disponible');

  //   if (newlyAddedClients.length === 0) {
  //     return null;
  //   }

  //   const existingClientCodes = new Set(existingSelectedClients.map(item => item.codigo_cliente));

  //   if (existingClientCodes.size === 0) {
  //     return null;
  //   }

  //   const mismatchedNewClients = newlyAddedClients.filter(item =>
  //     !existingClientCodes.has(item.codigo_cliente)
  //   );

  //   if (mismatchedNewClients.length > 0) {
  //     let message = 'Advertencia: Se están agregando clientes de diferentes tipos en la lista.\n\n';

  //     message += `Clientes con código diferente agregados:\n`;
  //     mismatchedNewClients.forEach(client => {
  //       message += `- ${client.razon_social} (${client.tipo_cliente})\n`;
  //     });

  //     message += '\nClientes en la lista original:\n';
  //     existingSelectedClients.forEach(client => {
  //       message += `- ${client.razon_social} (${client.tipo_cliente})\n`;
  //     });

  //     return message;
  //   }

  //   return null;
  // }
  eliminacion: boolean = false;

  guardar(){
    const clientesSeleccionados = this.selectedClients();
    const clientesDisponibles = this.allAvailableClients();

    const clientesAplanados = clientesSeleccionados.flatMap(item => {
      if (this.esGrupo(item)) {
        // Si es un grupo, devolver su array de clientes
        return item.clientes;
      } else {
        // Si es un cliente, devolverlo en un array para que flatMap lo maneje correctamente
        return [item];
      }
    });
    // const clientesSeleccionados = this.selectedClients().map(cliente => cliente.entC_Codigo);// array de los códigos
    const clientesParaEnviar = clientesAplanados.map(cliente => cliente.entC_Codigo);// array de los códigos

    const payload = {
      ...this.grupoForm.value,
      AUDI_UsrCrea: this.storage.get('codusuario'),
      lista_clientes: clientesParaEnviar
    };
    const grupo_id = payload.grupo_id;

    // =====================================================
    // 🔍 VALIDACIÓN: Grupos que quedarán con 1 o 0 clientes
    //                o que fueron movidos completamente
    // =====================================================
    const gruposAEliminar: { nombre: string; grupo_id: number }[] = [];

    const gruposSeleccionados = clientesSeleccionados.filter(c => this.esGrupo(c));
    const idsDisponibles = clientesDisponibles.map(g => g.grupo_id);


    // 1️⃣ Validar grupos que quedarán con 1 o 0 clientes
    clientesDisponibles.forEach(grupo => {
      if (this.esGrupo(grupo)) {
          //Si grupo.grupo_id está dentro de clientesSeleccionados, es decir si algunos de los objetos tiene el mismo grupo_id pues validar su cantidad, sino no.
          if(clientesSeleccionados.find(c => c.grupo_id === grupo.grupo_id)){
            // ✅ Solo marcar para eliminación si:
            // - el grupo quedará con 1 o 0 clientes
            // - y al menos un cliente del grupo fue movido a seleccionados
            if (grupo.clientes.length <= 1) {
              gruposAEliminar.push({
                nombre: grupo.nombre_grupo,
                grupo_id: grupo.grupo_id
              });
            }
          }
      }
    });

    // 2️⃣ Validar grupos completamente movidos

    gruposSeleccionados.forEach(grupoSel => {
      if (!idsDisponibles.includes(grupoSel.grupo_id)) {
        gruposAEliminar.push({
          nombre: grupoSel.nombre_grupo,
          grupo_id: grupoSel.grupo_id
        });
      }
    });

    // ✅ Si hay grupos a eliminar, mostrar advertencia antes de continuar
    if (gruposAEliminar.length > 0) {
      const nombres = gruposAEliminar.map(g => `• ${g.nombre}`).join('<br>');
      this.util.ShowMessage({
        titulo: 'Advertencia',
        mensaje: `Se eliminará automáticamente el(los) grupo(s):<br>${nombres}<br>porque no pueden quedar con menos de 2 clientes.`,
        tipo: 'warning',
        pregunta: {
          onAccept: () => {
            this.ejecutarGuardar(grupo_id, payload, clientesParaEnviar)
          },
          onReject: () => {

          }
        },
        textoBtn: {
          textoBtnOpcion1: 'Continuar',
          textoBtnOpcion2: 'Cancelar'
        }
      });
    }else{
      // Si no hay eliminaciones, sigue el flujo normal
      this.ejecutarGuardar(grupo_id, payload, clientesParaEnviar);
    }

  }

  ejecutarGuardar(grupo_id, payload, clientesParaEnviar){
    // ============================
    // CREACIÓN
    // ============================
    if (!grupo_id) {
      this.gruposService.CrearGrupoComercial(payload).subscribe({
        next: (res) => {
          const nuevoId = res.datos?.result[0]?.nuevo_grupo_id;
          const nombreGrupo = res.datos?.result[0]?.nombre_grupo;

          this.util.ShowMessage({
            titulo: 'Éxito',
            mensaje: `Grupo creado correctamente (Nombre: ${nombreGrupo})`,
            tipo: 'ok',
            autocerrar: true,
            tiempo: 4000
          });

          this.showDialog = false;
          this.buscarGrupos(nuevoId);
          this.dialogLoading.set(false);
        },
        error: (error) => {
          this.dialogLoading.set(false);
          this.handleGrupoError(error, 'crear');
        },
        complete: () => {
          this.resetFiltros();
        }
      });
      return;
    }

    // ============================
    // ACTUALIZACIÓN
    // ============================
    if (grupo_id) {
      // Define el mensaje de advertencia si corresponde
      if (clientesParaEnviar.length === 0) {
        this.mensajeActualizacion = 'Se eliminará la agrupación por falta de clientes.';
        this.eliminacion = true;
      } else if (clientesParaEnviar.length === 1) {
        this.mensajeActualizacion = 'No puede haber un solo cliente en el grupo. Se eliminará el grupo y el cliente quedará suelto.';
        this.eliminacion = true;
      } else {
        this.mensajeActualizacion = '¿Desea confirmar los cambios en el grupo?';
        this.eliminacion = false;
      }

      this.util.ShowMessage({
        titulo: 'Confirmar actualización',
        mensaje: this.mensajeActualizacion,
        tipo: 'warning',
        pregunta: {
          onAccept: () => {
            this.gruposService.ActualizarGrupoComercial(payload).subscribe({
              next: () => {
                // 🔹 Mostrar mensaje distinto según si se eliminó o no
                if (this.eliminacion) {
                  this.util.ShowMessage({
                    titulo: 'Grupo eliminado',
                    mensaje: 'El grupo fue eliminado correctamente porque debe tener más de un cliente.',
                    tipo: 'warning',
                    autocerrar: true,
                    tiempo: 5000
                  });
                } else {
                  this.util.ShowMessage({
                    titulo: 'Éxito',
                    mensaje: 'Grupo actualizado correctamente.',
                    tipo: 'ok',
                    autocerrar: true,
                    tiempo: 4000
                  });
                }

                this.showDialog = false;
                if (this.eliminacion) {
                  this.buscarGrupos(); // recarga todo
                } else {
                  this.buscarGrupos(grupo_id); // recarga solo ese grupo
                }
                this.dialogLoading.set(false);
              },
              error: (error) => {
                this.dialogLoading.set(false);
                this.handleGrupoError(error, 'actualizar');
              },
              complete: () => {
                this.resetFiltros();
              }
            });
          },
          onReject: () => {
            this.util.ShowMessage({
              titulo: 'Cancelado',
              mensaje: 'La actualización fue cancelada.',
              tipo: 'warning',
              autocerrar: true,
              tiempo: 3000
            });
          }
        },
        textoBtn: {
          textoBtnOpcion1: 'Sí, actualizar',
          textoBtnOpcion2: 'No, cancelar'
        }
      });
    }
  }

  private resetFiltros(): void {
    this.sourceFilter.set("");
    this.targetFilter.set("");
    this.clienteFilter.set("");
    this.filtroNoAsignadosPorGrupo.set(true);
    this.filtroNoAsignadosPorCliente.set(true);
  }

  // closeValidationDialog() {
  //   this.validationMessage.set(null);
  // }

  // Método para manejar la autenticación y el guardado
  onAuthConfirm() {
    // Validación de credenciales del administrador (simulado)
    if (this.adminUsername() === 'admin' && this.adminPassword() === 'password') {
      this.authError.set('');
      this.closeAuthDialog();
      // this.guardar();
    } else {
      this.authError.set('Usuario o contraseña incorrectos.');
    }
  }

  closeAuthDialog() {
    this.showAuthDialog.set(false);
    this.adminUsername.set('');
    this.adminPassword.set('');
    this.authError.set('');
    this.ejecutivosValidationMessage.set(null);
    this.gruposValidationMessage.set(null);
  }

  private handleGrupoError(error: any, accion: 'crear' | 'actualizar' | 'eliminar') {
    if (error.status === 400) {
      this.util.ShowMessage({
        titulo: 'Datos inválidos',
        mensaje: error.error?.meta?.Mensaje || `No se pudo ${accion} el grupo por datos inválidos.`,
        tipo: 'warning',
        autocerrar: true,
        tiempo: 5000,
      });
    } else if (error.status === 404) {
      this.util.ShowMessage({
        titulo: 'No encontrado',
        mensaje: `El grupo que intentas ${accion} no existe.`,
        tipo: 'warning',
        autocerrar: true,
        tiempo: 5000,
      });
    } else {
      this.util.ShowMessage({
        titulo: 'Error de Conexión',
        mensaje: `No se pudo conectar con el servidor para ${accion} el grupo.`,
        detalle: error.message || 'Error desconocido.',
        tipo: 'error',
        autocerrar: false,
      });
    }
  }

  dialogLoading = signal(false)
  cargarClientesDisponibles(grupo_id?: number) {
    this.dialogLoading.set(true)
    //Agregar loading para la carga del servicio si es necesario
    this.gruposService.ListarClientesComerciales({grupo_id}).subscribe({
      next: (res) => {
        if (res.datos.status === 200) {
          this.organizarListas(res.datos.result || []);// vendrá el tipado de la interfaz Clientes
          // Agrega el campo `filtro_cliente` a cada objeto
          const clientsWithFilter = res.datos.result.map((item: ClientesComerciales) => ({
            ...item,
            filtro_cliente: `${item.razon_social} - ${item.entC_DocIden} - ${item.nombre_grupo}`
          }));
          // const clientsAvailable = clientsWithFilter.filter((c: ClientesComerciales) => c.tipo === 'disponible');
          // clientsAvailable.map(c => ({
          //   ...c,
          //   expandido: c.grupo_id !== null
          // }))
          // this.allAvailableClients.set(clientsWithFilter.filter(c => c.tipo === 'disponible'));
          this.selectedClients.set(clientsWithFilter.filter(c => c.tipo === 'seleccionado'));
          console.log("selectedClients", this.selectedClients())
          this.dialogLoading.set(false)
        } else {
          console.error('No se pudieron cargar los clientes.');
        }

      },
      error: (error) => {
        if (error.status === 404) {
          this.util.ShowMessage({
            titulo: 'No se encontraron clientes',
            mensaje:
              'No se encontraron clientes con los criterios de búsqueda proporcionados.',
            tipo: 'warning',
            autocerrar: true,
            tiempo: 5000,
          });
        } else {
          this.util.ShowMessage({
            titulo: 'Error de Conexión',
            mensaje:
              'No se pudo conectar con el servidor para cargar los clientes.',
            detalle: error.message || 'Error desconocido.',
            tipo: 'error',
            autocerrar: false,
          });
        }
        this.allAvailableClients.set([]);
        this.selectedClients.set([]);
        this.dialogLoading.set(false)
      },
    }
    );
  }

  organizarListas(data: ClientesComerciales[]) {
    const separar = (tipo: string) => {
      const filtrados = data.filter(c => c.tipo === tipo);

      const gruposMap = new Map<number, ClientesComerciales[]>();
      const sinGrupo: ClientesComerciales[] = [];

      filtrados.forEach(c => {
        // Ahora verificamos grupo_id
        if (c.grupo_id && c.nombre_grupo) {
          if (!gruposMap.has(c.grupo_id)) {
            gruposMap.set(c.grupo_id, []);
          }
          gruposMap.get(c.grupo_id)!.push(c);
        } else {
          sinGrupo.push(c);
        }
      });

      const lista: ItemAsignacion[] = [];
      gruposMap.forEach((clientes, grupo_id) => {
        // El nombre_grupo se toma del primer cliente del grupo
        const nombre_grupo = clientes[0].nombre_grupo!;
        lista.push({
          grupo_id,
          nombre_grupo,
          expandido: false,
          clientes
        });
      });
      lista.push(...sinGrupo);

      return lista;
    };

    this.allAvailableClients.set(separar('disponible'));
    console.log("allAvailableClients ", this.allAvailableClients());
    // this.selectedClients.set(separar('seleccionado'));
  }

  // Función para verificar si un ítem es un Grupo
  esGrupo(item: ItemAsignacion): item is Grupo {
    // La verificación por la existencia de la propiedad 'clientes' es más fiable.
    return (item as Grupo).clientes !== undefined;
  }

  // Función de trackBy actualizada
  getTrackByValue(item: ItemAsignacion): number | string {
    if (this.esGrupo(item)) {
      return item.grupo_id;
    } else {
      return item.entC_Codigo;
    }
  }

  toggleGrupo(item: ItemAsignacion) {
    if (this.esGrupo(item)) {
      item.expandido = !item.expandido;
    }
  }

  exportMetadata: { field: string; header: string }[] = [
    { field: 'nombre_grupo', header: 'Nombre Grupo' }, // Para el nodo padre
    { field: 'razon_social', header: 'Razón Social' }, // Para el nodo hijo/cliente
    { field: 'entC_DocIden', header: 'RUC' },
    { field: 'estado_desc', header: 'Cliente' },
    { field: 'detalle_desc', header: 'Detalle' },
    { field: 'estado_sunat', header: 'Estado Sunat' },
    { field: 'nomCompletoEjecutivo', header: 'Ejecutivo' },
    { field: 'nomCompletoCustomer', header: 'Customer' },
    // Añade aquí cualquier otro campo relevante de tu data
  ];

  // Almacenará la lista plana de filas y la información de combinación
  private flattenAndTrackMerges(nodes: TreeNode[]): RowExportData[] {
    const flattened: RowExportData[] = [];
    let currentRowIndex = 0; // Seguimiento del índice de la fila en el array plano (solo clientes)

    // 🔹 Si todos los nodos son clientes sin grupo, mapeamos directamente
    const allClients = nodes.every(n => n.data?.tipo === 'cliente');
    if (allClients) {
      nodes.forEach(node => {
        const rowData = { ...node.data };

        // 👇 Este es el cambio importante:
        // Si es cliente sin grupo, usamos su propia razón social como “nombre_grupo”.
        rowData.nombre_grupo = rowData.razon_social || '';

        flattened.push({ data: rowData, isGroup: false });
        currentRowIndex++;
      });

      return flattened; // Salimos aquí, no hacemos merges
    }

    // 🔹 Caso normal: estructura con grupos y clientes
    nodes.forEach((node) => {
      if (!node.data) return;

      const isGroup = node.data.tipo === 'grupo';

      if (isGroup) {
        const groupName = node.data.nombre_grupo || '';

        // *** CAMBIO CLAVE: El índice de inicio de la combinación (groupStartIndex)
        // debe ser el índice de la primera fila del cliente. ***
        const groupStartIndex = currentRowIndex;

        // Manejar las filas de los Clientes (Hijos)
        if (node.children) {
          node.children.forEach(childNode => {
            if (childNode.data) {
              const clientRow: any = { ...childNode.data };

              // Los clientes deben tener el nombre del grupo en su campo 'nombre_grupo'
              // para que aparezca en la celda de inicio de la combinación.
              clientRow.nombre_grupo = groupName;

              flattened.push({
                data: clientRow,
                isGroup: false,
              });
              currentRowIndex++; // Solo incrementamos si agregamos una fila (un cliente)
            }
          });
        }

        // *** CAMBIO CLAVE: Calcular el índice final de la combinación (groupEndIndex). ***
        // Si se agregaron clientes, el índice final es la fila actual - 1.
        const numChildren = node.children?.length || 0;
        if (numChildren > 0) {
          const groupEndIndex = currentRowIndex - 1;

          // Almacenamos la información de combinación en el primer cliente del grupo
          // para que luego la función 'exportTreeTableData' la pueda extraer.
          flattened[groupStartIndex].isGroup = true; // Marcamos el primer cliente como el 'líder' del merge
          flattened[groupStartIndex].groupStartIndex = groupStartIndex;
          flattened[groupStartIndex].groupEndIndex = groupEndIndex;
          flattened[groupStartIndex].groupName = groupName;

        } else {
          // Manejar grupos sin clientes (si aplica)
          // Puedes optar por no exportar grupos sin clientes o exportar una fila vacía si es necesario
        }

      } else {
          // 🔹 Caso: cliente suelto (sin grupo)
          const clientRow: any = { ...node.data };

          // Si no tiene grupo, usar su razón social como nombre_grupo
          if (!clientRow.nombre_grupo || clientRow.nombre_grupo.trim() === '') {
            clientRow.nombre_grupo = clientRow.razon_social || '';
          }

          // ✅ Usamos el clientRow ya modificado, no node.data
          flattened.push({ data: clientRow, isGroup: false });
          currentRowIndex++;
      }
    });
    return flattened;
  }

  /**
   * Función que llama a la utilidad de exportación con los datos aplanados.
   */
  exportTreeTableData(): void {
    const treeData = this.gruposTree();
    if (!treeData || treeData.length === 0) {
      console.warn('No hay datos en el TreeTable para exportar.');
      return;
    }

    // Aplanar los datos y obtener la información de combinación
    const flattenedAndMergedData = this.flattenAndTrackMerges(treeData);

    let dataToExport: any[] = [];
    let mergeInfo: { s: { r: number, c: number }, e: { r: number, c: number } }[] = [];
    const startRowOffset = 1; // Tu función NewExcelSheet empieza los datos en la fila 2 (índice 1, después de la cabecera)

    flattenedAndMergedData.forEach((item, index) => {
      const row = item.data;
      const newRow = {};

      this.exportMetadata.forEach((meta) => {
        const headerKey = meta.header.toUpperCase();
        const value = row[meta.field] !== undefined && row[meta.field] !== null ? row[meta.field] : '';
        newRow[headerKey] = value;
      });

      dataToExport.push(newRow);

      // Si es el 'líder' del merge (el primer cliente del grupo), calcula la información de combinación
      if (item.isGroup && item.groupStartIndex !== undefined && item.groupEndIndex !== undefined) {
        mergeInfo.push({
          s: { r: item.groupStartIndex + startRowOffset, c: 0 }, // Inicio: Primera fila del cliente, Columna 0
          e: { r: item.groupEndIndex + startRowOffset, c: 0 }, // Fin: Última fila del cliente, Columna 0
        });
      }
    });

    // 3. Llamar a la utilidad de exportación, pasando la información de combinación
    const data = [{ data: dataToExport, cabeceras: [] }];
    const fileName = 'Grupos Comerciales' + '.xlsx';

    // Modificamos la llamada para pasar las combinaciones
    this.util.ExportarExcelTreeTable(data, fileName, false, [], mergeInfo);
  }
}
